import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GoogleButton } from "../components/GoogleButton";
import { AmountInput } from "../components/AmountInput";

function OfferBanner({ offer }) {
  if (!offer) return null;
  const labels = { starter: "Starter — 199 999 GNF/mois", pro: "Pro — 349 999 GNF/mois", business: "Business — 999 999 GNF/mois" };
  if (!labels[offer]) return null;
  return (
    <div data-testid="selected-offer-banner" className="kcard px-4 py-3 mb-3 flex items-center gap-2">
      <SealCheck size={18} weight="fill" color="#C9793A" className="shrink-0" />
      <span className="text-sm">Offre choisie : <span className="font-semibold">{labels[offer]}</span></span>
    </div>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const offer = params.get("offre") || "";
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ company_name: "", sector: "", email: "", phone: "", code: "", password: "", starting_cash: "", stock_mode: "with_stock", currency: "GNF" });
  const [masked, setMasked] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const stepOne = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/register/init", {
        company_name: form.company_name, sector: form.sector, email: form.email, phone: form.phone });
      setMasked(data.email_masked || form.email);
      setStep(2);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const stepTwo = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/register/complete", {
        email: form.email, phone: form.phone, code: form.code, password: form.password,
        starting_cash: parseFloat(form.starting_cash) || 0, stock_mode: form.stock_mode,
        currency: form.currency, offer });
      login(data);
      if (data.user?.subscription?.payment_status === "pending") {
        navigate(`/paiement?offre=${offer || "starter"}`);
        return;
      }
      navigate("/accueil");
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md fade-in">
        <p className="font-display font-bold text-4xl lowercase tracking-tight text-center mb-8">kavizio</p>
        <OfferBanner offer={offer} />
        {step === 1 && (
          <form onSubmit={stepOne} className="kcard p-6 space-y-4">
            <h1 className="font-display font-bold text-xl">Créer mon entreprise</h1>
            {error && <p data-testid="register-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}
            <input data-testid="register-company-input" className="kinput" placeholder="Nom de l'entreprise" value={form.company_name} onChange={set("company_name")} required />
            <input data-testid="register-sector-input" className="kinput" placeholder="Secteur d'activité (ex. commerce, pharmacie)" value={form.sector} onChange={set("sector")} />
            <input data-testid="register-email-input" className="kinput" type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
            <input data-testid="register-phone-input" className="kinput" placeholder="Numéro de téléphone" value={form.phone} onChange={set("phone")} required />
            <button data-testid="register-step1-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Envoi..." : "Recevoir le code par email"}</button>
            <GoogleButton offer={offer} label="S'inscrire avec Google" />
            <p className="text-sm text-center text-gray-500">
              Déjà inscrit ? <Link to="/connexion" className="text-[#C9793A] font-semibold">Se connecter</Link>
            </p>
          </form>
        )}
        {step === 2 && (
          <form onSubmit={stepTwo} className="kcard p-6 space-y-4">
            <h1 className="font-display font-bold text-xl">Vérification & finalisation</h1>
            <div data-testid="email-code-banner" className="bg-[#F6F4EF] text-gray-600 text-sm rounded-xl px-3 py-2">
              Un code de vérification a été envoyé à <span className="font-semibold">{masked}</span>. Il est valable 15 minutes.
            </div>
            {error && <p data-testid="register-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}
            <input data-testid="register-code-input" className="kinput font-mono-doc" placeholder="Code reçu par email" value={form.code} onChange={set("code")} required />
            <input data-testid="register-password-input" className="kinput" type="password" placeholder="Mot de passe (6 caractères min)" value={form.password} onChange={set("password")} required minLength={6} />
            <AmountInput data-testid="register-cash-input" onOverflow={(m) => toast.error(m)} placeholder="Trésorerie de départ (optionnel)" value={form.starting_cash} onChange={(v) => setForm((f) => ({ ...f, starting_cash: v }))} />
            <div>
              <p className="text-sm font-semibold mb-2">Devise de votre entreprise</p>
              <p className="text-xs text-gray-500 mb-2">Toute votre activité (ventes, dépenses, documents) sera affichée dans cette devise. Modifiable plus tard.</p>
              <div className="grid grid-cols-2 gap-2">
                {[["GNF", "Franc Guinéen", "GNF"], ["XOF", "Franc CFA", "FCFA"],
                  ["EUR", "Euro", "€"], ["USD", "Dollar US", "$"]].map(([code, label, sym]) => (
                  <button type="button" key={code} data-testid={`currency-${code}`}
                    onClick={() => setForm({ ...form, currency: code })}
                    className={`text-left p-3 rounded-2xl border transition-colors ${form.currency === code ? "border-[#C9793A] bg-[#C9793A]/5" : "border-[#E0DACE] bg-white"}`}>
                    <span className="text-sm font-semibold block">{sym}</span>
                    <span className="text-xs text-gray-500">{label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">Gestion du stock</p>
              <div className="grid grid-cols-2 gap-2">
                {[["with_stock", "Avec stock", "Ventes détaillées produit par produit"], ["no_stock", "Sans stock", "Montant global, sans quantités"]].map(([v, t, d]) => (
                  <button type="button" key={v} data-testid={`stock-mode-${v}`}
                    onClick={() => setForm({ ...form, stock_mode: v })}
                    className={`text-left p-3 rounded-2xl border transition-colors ${form.stock_mode === v ? "border-[#C9793A] bg-[#C9793A]/5" : "border-[#E0DACE] bg-white"}`}>
                    <span className="text-sm font-semibold block">{t}</span>
                    <span className="text-xs text-gray-500">{d}</span>
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="register-step2-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Création..." : "Créer mon compte"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
